/**
 * @file StatusBadge.tsx
 *
 * Reusable status pill: a Gravity colour indicator dot followed by a label.
 * Domain-agnostic — callers pass the resolved `gv-state-*` indicator class and
 * the localized label (e.g. the Orders tab maps an order status via
 * `orders/order-status.ts`). Mirrors the inline indicator markup the product
 * details drawer uses, extracted here so every tab shares one pill.
 */

import type { ReactNode } from 'react';

interface StatusBadgeProps {
  /** The Gravity state class for the dot, e.g. `"gv-state-positive"`. */
  indicator: string;
  /** The localized status label. */
  label: ReactNode;
}

/**
 * Renders a coloured status indicator dot with a label.
 *
 * @param props.indicator - The `gv-state-*` class controlling the dot colour.
 * @param props.label - The localized label shown beside the dot.
 *
 * @returns The status pill element.
 */
export function StatusBadge({ indicator, label }: StatusBadgeProps) {
  return (
    <span className="gv-text-indicator">
      <span className={`gv-indicator ${indicator}`} />
      <span className="gv-text-sm">{label}</span>
    </span>
  );
}
